import React, { useState, useEffect, useRef } from 'react';
import { accountsPayableAPI, businessesAPI, creditorsAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import { formatApiError } from '../utils/errorMessage';
import { newIdempotencyKey, isPaymentUnconfirmed, PAYMENT_UNCONFIRMED, PAYMENT_REPLAYED } from '../utils/idempotencyKey';
import { todayLocal, parseCalendarDay, calendarDayKey } from '../utils/localDate';
import { parseMoneyInput, MONEY_FIELD_ERROR } from '../utils/money';

/**
 * Accounts Payable Page
 * Single Responsibility: Manage vendor bills and payments
 */
const AccountsPayable = ({ selectedBusiness: propSelectedBusiness }) => {
  const [apList, setApList] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [agingReport, setAgingReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [paying, setPaying] = useState(false);
  const paymentKey = useRef(null);
  const [localBusiness, setLocalBusiness] = useState(propSelectedBusiness || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [view, setView] = useState('list'); // 'list' or 'aging'
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAP, setSelectedAP] = useState(null);
  const [formData, setFormData] = useState({
    business_id: propSelectedBusiness || '',
    vendor_id: '',
    vendor_name: '',
    vendor_email: '',
    vendor_phone: '',
    vendor_address: '',
    bill_number: '',
    bill_date: todayLocal(),
    due_date: '',
    amount: '',
    payment_terms: 'Net 30',
    description: '',
    notes: '',
    category_id: ''
  });
  const [paymentData, setPaymentData] = useState({
    payment_date: todayLocal(),
    amount: '',
    payment_method: 'check',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [localBusiness, statusFilter, vendorFilter, showOverdueOnly]);

  useEffect(() => {
    setLocalBusiness(propSelectedBusiness || '');
  }, [propSelectedBusiness]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (localBusiness) params.business_id = localBusiness;
      if (statusFilter) params.status = statusFilter;
      if (vendorFilter) params.vendor_name = vendorFilter;
      if (showOverdueOnly) params.overdue_only = 'true';

      const [apRes, businessRes, summaryRes, agingRes, vendorsRes] = await Promise.all([
        accountsPayableAPI.getAll(params),
        businessesAPI.getAll(),
        accountsPayableAPI.getSummary(params),
        accountsPayableAPI.getAging(params),
        creditorsAPI.getAll().catch(() => ({ data: [] }))
      ]);

      setApList(apRes.data);
      setBusinesses(businessRes.data);
      setSummary(summaryRes.data);
      setAgingReport(agingRes.data);
      setVendors(vendorsRes.data);
      setError('');
    } catch (err) {
      setError(formatApiError(err, 'Failed to load accounts payable data'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseMoneyInput(formData.amount);
    if (amount.error || amount.value === null) {
      setError(`Amount: ${MONEY_FIELD_ERROR}`);
      return;
    }
    const billData = { ...formData, amount: amount.value };
    try {
      if (selectedAP) {
        await accountsPayableAPI.update(selectedAP.id, billData);
      } else {
        await accountsPayableAPI.create(billData);
      }
      resetForm();
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(formatApiError(err, 'Failed to save bill'));
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (paying) return;
    const amount = parseMoneyInput(paymentData.amount);
    if (amount.error || amount.value === null) {
      setError(`Amount: ${MONEY_FIELD_ERROR}`);
      return;
    }
    if (!paymentKey.current) paymentKey.current = newIdempotencyKey();
    setPaying(true);
    try {
      const response = await accountsPayableAPI.recordPayment(selectedAP.id, {
        ...paymentData,
        amount: amount.value,
        idempotency_key: paymentKey.current
      });
      paymentKey.current = null;
      setNotice(response.data?.replayed ? PAYMENT_REPLAYED : '');
      setShowPaymentModal(false);
      setPaymentData({
        payment_date: todayLocal(),
        amount: '',
        payment_method: 'check',
        reference_number: '',
        notes: ''
      });
      loadData();
    } catch (err) {
      if (isPaymentUnconfirmed(err)) setError(PAYMENT_UNCONFIRMED);
      else setError(formatApiError(err, 'Failed to record payment'));
    } finally {
      setPaying(false);
    }
  };

  const closePaymentModal = () => {
    paymentKey.current = null;
    setShowPaymentModal(false);
  };

  const openEditModal = (ap) => {
    setSelectedAP(ap);
    setFormData({
      business_id: ap.business_id || '',
      vendor_id: ap.vendor_id || '',
      vendor_name: ap.vendor_name || '',
      vendor_email: ap.vendor_email || '',
      vendor_phone: ap.vendor_phone || '',
      vendor_address: ap.vendor_address || '',
      bill_number: ap.bill_number || '',
      bill_date: ap.bill_date ? calendarDayKey(ap.bill_date) : '',
      due_date: ap.due_date ? calendarDayKey(ap.due_date) : '',
      amount: ap.amount || '',
      payment_terms: ap.payment_terms || 'Net 30',
      description: ap.description || '',
      notes: ap.notes || '',
      category_id: ap.category_id || ''
    });
    setShowModal(true);
  };

  const openPaymentModal = (ap) => {
    paymentKey.current = newIdempotencyKey();
    setNotice('');
    setSelectedAP(ap);
    setPaymentData({
      ...paymentData,
      amount: ap.amount_remaining ?? ''
    });
    setShowPaymentModal(true);
  };

  const resetForm = () => {
    setSelectedAP(null);
    setFormData({
      business_id: localBusiness || '',
      vendor_id: '',
      vendor_name: '',
      vendor_email: '',
      vendor_phone: '',
      vendor_address: '',
      bill_number: '',
      bill_date: todayLocal(),
      due_date: '',
      amount: '',
      payment_terms: 'Net 30',
      description: '',
      notes: '',
      category_id: ''
    });
  };

  const getStatusBadge = (ap) => {
    const isOverdue = ap.is_overdue || (parseCalendarDay(ap.due_date) < new Date() && ap.amount_paid < ap.amount);
    if (isOverdue) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Overdue</span>;
    }
    switch (ap.status) {
      case 'paid':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Paid</span>;
      case 'partially_paid':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Partial</span>;
      case 'void':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Void</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Outstanding</span>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getAgingDays = (ap) => {
    if (ap.aging_days !== undefined && ap.aging_days !== null) return ap.aging_days;
    if (!ap.due_date) return 0;
    const dueDate = parseCalendarDay(ap.due_date);
    if (isNaN(dueDate.getTime())) return 0;
    const today = new Date();
    return Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
  };

  if (loading && !apList.length) {
    return <div className="flex justify-center items-center h-64"><div className="text-gray-500">Loading...</div></div>;
  }

  return (
    <ErrorBoundary section="Accounts Payable">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Accounts Payable</h1>
            <p className="text-gray-600 mt-1">Manage vendor bills and track payments</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + New Bill
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">
            {error}
          </div>
        )}

        {notice && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded" role="status">
            {notice}
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Bills</div>
              <div className="text-2xl font-bold text-gray-800">{formatCurrency(summary.total_amount)}</div>
              <div className="text-xs text-gray-500 mt-1">{summary.total_bills} bills</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Paid</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_paid)}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Outstanding</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.total_remaining)}</div>
              <div className="text-xs text-gray-500 mt-1">{summary.outstanding_count} bills</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Overdue</div>
              <div className="text-2xl font-bold text-red-600">{summary.overdue_count || 0}</div>
              <div className="text-xs text-gray-500 mt-1">bills</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Paid</div>
              <div className="text-2xl font-bold text-gray-600">{summary.total_bills - summary.outstanding_count}</div>
              <div className="text-xs text-gray-500 mt-1">bills</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-center">
          <select
            value={localBusiness}
            onChange={(e) => setLocalBusiness(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="">All Businesses</option>
            {businesses.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="">All Statuses</option>
            <option value="outstanding">Outstanding</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <select
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="">All Vendors</option>
            {vendors.map(v => (
              <option key={v.id} value={v.creditor_name}>{v.creditor_name}</option>
            ))}
          </select>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showOverdueOnly}
              onChange={(e) => setShowOverdueOnly(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Overdue Only</span>
          </label>
          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 rounded ${view === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Bill List
            </button>
            <button
              onClick={() => setView('aging')}
              className={`px-4 py-2 rounded ${view === 'aging' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Aging Report
            </button>
          </div>
        </div>

        {/* Bill List View */}
        {view === 'list' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remaining</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aging</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {apList.map(ap => {
                    const amountRemaining = ap.amount_remaining ?? 0;
                    const agingDays = getAgingDays(ap);
                    return (
                      <tr key={ap.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {ap.bill_number || `AP-${ap.id}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{ap.vendor_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ap.bill_date ? parseCalendarDay(ap.bill_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ap.due_date ? parseCalendarDay(ap.due_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(ap.amount)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(ap.amount_paid || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-semibold">{formatCurrency(amountRemaining)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`font-semibold ${agingDays > 90 ? 'text-red-600' : agingDays > 60 ? 'text-orange-600' : agingDays > 30 ? 'text-yellow-600' : 'text-green-600'}`}>
                            {agingDays} days
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(ap)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {ap.status !== 'paid' && (
                            <button
                              onClick={() => openPaymentModal(ap)}
                              className="text-green-600 hover:text-green-900 mr-2"
                            >
                              Pay
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(ap)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {apList.length === 0 && (
              <div className="text-center py-8 text-gray-500">No bills found</div>
            )}
          </div>
        )}

        {/* Aging Report View */}
        {view === 'aging' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bills</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current (0-30)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">31-60 Days</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">61-90 Days</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Over 90 Days</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agingReport.map(vendor => (
                    <tr key={vendor.vendor_name}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{vendor.vendor_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{vendor.bill_count}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{formatCurrency(vendor.total_remaining)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(vendor.current_0_30)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600">{formatCurrency(vendor.aging_31_60)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">{formatCurrency(vendor.aging_61_90)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">{formatCurrency(vendor.aging_over_90)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {agingReport.length === 0 && (
              <div className="text-center py-8 text-gray-500">No aging data available</div>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">{selectedAP ? 'Edit Bill' : 'New Bill'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business</label>
                    <select
                      value={formData.business_id}
                      onChange={(e) => setFormData({ ...formData, business_id: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="">Select business</option>
                      {businesses.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                    <select
                      value={formData.vendor_id}
                      onChange={(e) => {
                        const vendor = vendors.find(v => v.id === parseInt(e.target.value));
                        setFormData({
                          ...formData,
                          vendor_id: e.target.value,
                          vendor_name: vendor ? vendor.creditor_name : formData.vendor_name,
                          vendor_email: vendor ? vendor.contact_email : formData.vendor_email,
                          vendor_phone: vendor ? vendor.contact_phone : formData.vendor_phone,
                          vendor_address: vendor ? vendor.mailing_address : formData.vendor_address
                        });
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="">Select vendor</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>{v.creditor_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.vendor_name}
                      onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bill Number</label>
                    <input
                      type="text"
                      value={formData.bill_number}
                      onChange={(e) => setFormData({ ...formData, bill_number: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bill Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.bill_date}
                      onChange={(e) => setFormData({ ...formData, bill_date: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      required
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                    <select
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="Due on Receipt">Due on Receipt</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 60">Net 60</option>
                      <option value="Net 90">Net 90</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {selectedAP ? 'Update' : 'Create'} Bill
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedAP && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Record Payment</h2>
              <div className="text-sm text-gray-600 mb-4">Bill: {selectedAP.bill_number || `AP-${selectedAP.id}`}</div>
              <form onSubmit={handlePayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                  <input
                    type="date"
                    required
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    required
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                  <select
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="check">Check</option>
                    <option value="wire">Wire Transfer</option>
                    <option value="ach">ACH</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <input
                    type="text"
                    value={paymentData.reference_number}
                    onChange={(e) => setPaymentData({ ...paymentData, reference_number: e.target.value })}
                    placeholder="Check #, Transaction ID, etc."
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closePaymentModal}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={paying}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                  >
                    Record Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default AccountsPayable;

